import React, { useState } from 'react';
import { Box, Button, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import '../../../resources/global.css';
import Loader from '../loader';

function ClientSearchAndList({
  clients = [],
  loading = false,
  pageIndex = 0,
  totalPages = 1,
  totalCount = 0,
  onSearch,
  onPageChange,
  onClear,
  onViewClient
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = () => {
    setHasSearched(true);

    const payload = {
      term: searchTerm || '',
      pageIndex: 0
    };

    onSearch?.(payload);
  };

  const handleClear = () => {
    setSearchTerm('');
    setHasSearched(false);
    onClear?.();
  };

  const handlePageChange = (newPageIndex) => {
    onPageChange?.({
      term: searchTerm,
      pageIndex: newPageIndex
    });
  };

  const formatName = (name = '') => {
    return name
      .toLowerCase()
      .split(' ')
      .map((word, index) => {
        if (index === 0 && word.length < 4) {
          return word.toUpperCase();
        }

        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  };

  const startItem = pageIndex * 8 + 1;
  const endItem = Math.min((pageIndex + 1) * 8, totalCount);

  return (
    <Box width="100%">
      {/* SEARCH */}
      <Box
        width="100%"
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: '16px'
        }}>
        <TextField
          fullWidth
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Search clients..."
          variant="outlined"
          slotProps={{
            input: {
              startAdornment: (
                <SearchIcon
                  sx={{
                    color: 'var(--ft-dark)',
                    fontSize: '20px',
                    marginRight: '12px'
                  }}
                />
              )
            }
          }}
          sx={{
            flex: { xs: 'none', md: 2 },
            '& .MuiOutlinedInput-root': {
              height: '56px',
              borderRadius: 0,
              fontFamily: 'var(--ft-body-font-family)',
              fontSize: '14px',
              fontWeight: 400,
              color: 'var(--ft-dark)',

              '& fieldset': {
                borderColor: 'var(--ft-border)'
              },

              '&:hover fieldset': {
                borderColor: 'var(--ft-border)'
              },

              '&.Mui-focused fieldset': {
                borderColor: 'var(--ft-orange)'
              }
            },

            '& .MuiInputBase-input::placeholder': {
              color: 'var(--ft-dark)',
              opacity: 1,
              fontFamily: 'var(--ft-body-font-family)',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '100%'
            }
          }}
        />

        {/* BUTTONS */}
        <Box
          sx={{
            display: 'flex',
            width: { xs: '100%', md: 'auto' },
            flex: { xs: 'none', md: 1 },
            gap: '16px'
          }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleClear}
            sx={{
              flex: 1,
              minWidth: 0,
              height: '56px',
              borderRadius: 0,
              border: '1px solid var(--ft-orange)',
              color: 'var(--ft-orange)',
              backgroundColor: 'var(--ft-white)',
              fontFamily: 'var(--ft-body-font-family)',
              fontSize: 'var(--ft-body-font-size)',
              fontWeight: 600,
              textTransform: 'uppercase',

              '&:hover': {
                border: '1px solid var(--ft-orange-hover)',
                backgroundColor: 'var(--ft-light-grey)'
              }
            }}>
            Clear
          </Button>

          <Button
            fullWidth
            variant="contained"
            onClick={handleSearch}
            sx={{
              flex: 1,
              minWidth: 0,
              height: '56px',
              borderRadius: 0,
              backgroundColor: 'var(--ft-orange)',
              color: 'var(--ft-white)',
              fontFamily: 'var(--ft-body-font-family)',
              fontSize: 'var(--ft-body-font-size)',
              fontWeight: 600,
              textTransform: 'uppercase',
              boxShadow: 'none',

              '&:hover': {
                backgroundColor: 'var(--ft-orange-hover)',
                boxShadow: 'none'
              }
            }}>
            Search
          </Button>
        </Box>
      </Box>

      {/* LOADING */}
      {hasSearched && loading && (
        <Box
          sx={{
            width: '100%',
            mt: '16px',
            backgroundColor: 'var(--ft-white)',
            padding: '24px'
          }}>
          <Loader loading={true} message="  Searching ..." />
        </Box>
      )}

      {/* RESULTS */}
      {hasSearched && !loading && clients.length > 0 && (
        <Box
          sx={{
            width: '100%',
            mt: '16px'
          }}>
          <Box
            sx={{
              width: '100%',
              backgroundColor: 'var(--ft-white)'
            }}>
            {clients.map((client) => (
              <Box
                key={client.id}
                onClick={() => onViewClient?.(client)}
                sx={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '20px 24px',
                  cursor: onViewClient ? 'pointer' : 'default',

                  '&:hover': onViewClient
                    ? {
                        backgroundColor: 'var(--ft-light-grey)'
                      }
                    : {}
                }}>
                {/* CLIENT NAME */}
                <Box
                  sx={{
                    color: 'var(--ft-dark)',
                    fontFamily: 'Gotham, sans-serif',
                    fontSize: '14px',
                    fontWeight: 350,
                    fontStyle: 'normal',
                    lineHeight: '100%',
                    letterSpacing: 0
                  }}>
                  {formatName(client.name)}
                </Box>

                {/* CLIENT ID */}
                <Box
                  sx={{
                    marginTop: '8px',
                    color: 'var(--ft-dark)',
                    fontFamily: 'var(--ft-body-font-family)',
                    fontSize: '14px',
                    fontWeight: 'var(--ft-body-font-weight)',
                    fontStyle: 'normal',
                    lineHeight: '16px',
                    letterSpacing: 0
                  }}>
                  ID: {client.clientId}
                </Box>
              </Box>
            ))}
          </Box>

          {/* PAGINATION */}
          {totalCount > 0 && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mt: '16px',
                padding: '20px 24px',
                boxSizing: 'border-box',
                backgroundColor: 'var(--ft-white)'
              }}>
              {/* RESULT RANGE */}
              <Box
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '100%',
                  letterSpacing: 0,
                  color: 'var(--ft-dark)'
                }}>
                {`${startItem}-${endItem} of ${totalCount} clients`}
              </Box>

              {/* PREV / NEXT */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '24px'
                }}>
                <Button
                  disabled={pageIndex === 0}
                  onClick={() => handlePageChange(pageIndex - 1)}
                  sx={{
                    minWidth: 0,
                    padding: 0,
                    borderRadius: 0,
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontStyle: 'normal',
                    fontSize: '12px',
                    lineHeight: '100%',
                    letterSpacing: 0,
                    color: 'var(--ft-dark)',
                    textTransform: 'none',

                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: 'var(--ft-orange)'
                    },

                    '&.Mui-disabled': {
                      color: 'var(--ft-border)'
                    }
                  }}>
                  Prev
                </Button>

                <Button
                  disabled={pageIndex >= totalPages - 1}
                  onClick={() => handlePageChange(pageIndex + 1)}
                  sx={{
                    minWidth: 0,
                    padding: 0,
                    borderRadius: 0,
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontStyle: 'normal',
                    fontSize: '12px',
                    lineHeight: '100%',
                    letterSpacing: 0,
                    color: 'var(--ft-dark)',
                    textTransform: 'none',

                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: 'var(--ft-orange)'
                    },

                    '&.Mui-disabled': {
                      color: 'var(--ft-border)'
                    }
                  }}>
                  Next
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* NO RESULTS */}
      {hasSearched && !loading && clients.length === 0 && (
        <Box
          sx={{
            width: '100%',
            mt: '16px',
            padding: '24px',
            backgroundColor: 'var(--ft-white)',
            color: 'var(--ft-dark)',
            fontFamily: 'var(--ft-body-font-family)',
            fontSize: 'var(--ft-body-font-size)'
          }}>
          No Results Found
        </Box>
      )}
    </Box>
  );
}

export default ClientSearchAndList;
